import { useEffect, useState } from 'react';
import type { Content } from '../data/content';
import { useWallet } from '../lib/wallet';
import { usdcAmount, type PaymentOption } from '../lib/x402';
import { cents } from '../lib/format';

type Phase = 'choose' | 'settling' | 'done';

/**
 * The unlock moment — a small bottom-sheet framed as backing a creator, not a
 * checkout. Two payment choices (Credits / Wallet) mirror the 402 `accepts`
 * options. One confident confirm, then a settle() call and a quick affirmation
 * before the content reveals in place. No forms, no friction.
 */
export function UnlockSheet({
  content,
  onClose,
  onUnlocked,
}: {
  content: Content;
  onClose: () => void;
  onUnlocked: () => void;
}) {
  const { credits, walletConnected, connectWallet, unlock, optionsFor } = useWallet();
  const options = optionsFor(content);
  const creditsOption = options.find((o) => o.scheme === 'credits') as
    | Extract<PaymentOption, { scheme: 'credits' }>
    | undefined;
  const cryptoOption = options.find((o) => o.scheme === 'exact') as
    | Extract<PaymentOption, { scheme: 'exact' }>
    | undefined;

  const canAffordCredits = !!creditsOption && credits >= content.priceCents;
  const [method, setMethod] = useState<'credits' | 'wallet'>(canAffordCredits ? 'credits' : 'wallet');
  const [phase, setPhase] = useState<Phase>('choose');

  // Close on Escape (but not mid-settle).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && phase !== 'settling') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, phase]);

  async function confirm() {
    const option = method === 'credits' ? creditsOption : cryptoOption;
    if (!option) return;
    setPhase('settling');
    await unlock(content, option);
    setPhase('done');
    // Let the affirmation land, then reveal in place.
    const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.setTimeout(onUnlocked, reduce ? 0 : 900);
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center sm:items-center"
      role="dialog"
      aria-modal="true"
      aria-label={`Back @${content.creator}`}
    >
      <button
        type="button"
        aria-label="Close"
        onClick={() => phase !== 'settling' && onClose()}
        className="absolute inset-0 bg-provny-ink/70 backdrop-blur-sm motion-safe:animate-[fadeIn_0.2s_ease]"
      />

      <div className="relative w-full max-w-md rounded-t-3xl border border-provny-border bg-provny-white p-6 text-provny-black shadow-2xl motion-safe:animate-[sheetUp_0.28s_cubic-bezier(0.16,1,0.3,1)] dark:border-provny-border-dark dark:bg-provny-surface-dark dark:text-provny-white sm:rounded-3xl">
        {phase === 'done' ? (
          <Affirmation creator={content.creator} amount={cents(content.priceCents)} />
        ) : (
          <>
            <p className="text-xs font-medium uppercase tracking-wider text-provny-muted">
              {content.channel}
            </p>
            <h2 className="mt-1 font-heading text-2xl font-black tracking-tight">{content.title}</h2>
            <p className="mt-1 text-provny-muted">
              Back <span className="font-semibold text-provny-black dark:text-provny-white">@{content.creator}</span> and unlock this {content.type}.
            </p>

            <div className="mt-5 flex flex-col gap-2.5">
              <MethodOption
                selected={method === 'credits'}
                disabled={!canAffordCredits}
                onClick={() => setMethod('credits')}
                title="Credits"
                detail={
                  canAffordCredits
                    ? `Balance ${credits} · costs ${creditsOption?.maxAmountRequired}`
                    : 'Not enough credits'
                }
                amount={cents(content.priceCents)}
              />
              <MethodOption
                selected={method === 'wallet'}
                onClick={() => setMethod('wallet')}
                title={walletConnected ? 'Wallet' : 'Connect wallet'}
                detail={
                  walletConnected
                    ? `${cryptoOption?.maxAmountRequired} USDC on Solana`
                    : 'Pay with USDC — tap to connect'
                }
                amount={`${usdcAmount(content.priceCents)} USDC`}
              />
            </div>

            {method === 'wallet' && !walletConnected ? (
              <button
                type="button"
                onClick={connectWallet}
                className="mt-5 w-full rounded-xl border border-provny-cyber-dim py-3 font-semibold text-provny-cyber-dim transition hover:bg-provny-cyber/10 dark:border-provny-cyber dark:text-provny-cyber"
              >
                Connect wallet
              </button>
            ) : (
              <button
                type="button"
                onClick={confirm}
                disabled={phase === 'settling'}
                className="mt-5 flex w-full items-center justify-center gap-2 rounded-xl bg-provny-cyber py-3 font-semibold text-provny-ink transition hover:bg-provny-cyber/90 disabled:cursor-wait disabled:opacity-80"
              >
                {phase === 'settling' ? (
                  <>
                    <Spinner /> Backing @{content.creator}…
                  </>
                ) : (
                  `Back this · ${cents(content.priceCents)}`
                )}
              </button>
            )}

            <p className="mt-3 text-center text-xs text-provny-muted">
              Your support goes straight to the creator.
            </p>
          </>
        )}
      </div>
    </div>
  );
}

function MethodOption({
  selected,
  disabled,
  onClick,
  title,
  detail,
  amount,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  title: string;
  detail: string;
  amount: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-pressed={selected}
      className={`flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition disabled:opacity-40 ${
        selected
          ? 'border-provny-cyber-dim bg-provny-cyber/10 dark:border-provny-cyber'
          : 'border-provny-border hover:border-provny-muted dark:border-provny-border-dark'
      }`}
    >
      <span>
        <span className="block text-sm font-semibold">{title}</span>
        <span className="block text-xs text-provny-muted">{detail}</span>
      </span>
      <span className="font-mono text-sm font-medium">{amount}</span>
    </button>
  );
}

/** The reveal payoff — a classy "you backed @creator" tick, not a casino. */
function Affirmation({ creator, amount }: { creator: string; amount: string }) {
  return (
    <div className="flex flex-col items-center py-6 text-center">
      <span className="flex h-14 w-14 items-center justify-center rounded-full bg-provny-gold/15 text-provny-gold motion-safe:animate-[pop_0.4s_cubic-bezier(0.16,1,0.3,1)]">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <path d="M20 6L9 17l-5-5" />
        </svg>
      </span>
      <p className="mt-4 font-heading text-xl font-black tracking-tight">You backed @{creator}</p>
      <p className="mt-1 text-sm text-provny-muted">{amount} well spent. Unlocking…</p>
    </div>
  );
}

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin" viewBox="0 0 24 24" fill="none">
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" />
      <path className="opacity-90" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.4 0 0 5.4 0 12h4z" />
    </svg>
  );
}
